import type {
  DocumentPageMetadataUserEditComment,
  DocumentPageMetadataUserEditEntry,
  DocumentPageMetadataUserEditSource,
  DocumentPageMetadataUserEditStatus,
  DocumentPageMetadataUserEditSurface,
  DocumentPageMetadataUserEditValues,
} from 'features/knowledgeDocumentViewer/types/documentMetadataUserEditSchemas'
import type { SystemDocumentPageEntry } from 'features/knowledgeDocumentViewer/types/systemDocumentPageSchemas'
import {
  bareDocumentPageId,
  cantileverDocumentEntryId,
  cantileverDocumentPageEntryId,
} from 'features/knowledgeDocumentViewer/utils/cantileverHostIds'
import { mergePageMetadataValues } from 'features/knowledgeDocumentViewer/utils/effectiveMetadata'
import {
  buildDocumentPageMetadataUserEditValues,
  buildFullDocumentPageMetadataUserEditValues,
  buildGeneratedPageMetadataValues,
} from 'features/knowledgeDocumentViewer/utils/metadataUserEditWrite'
import { type RolodexEntryPutJson, RolodexEntryType } from 'types'

const PIPELINE_CONFIG_TAG = 'pipeline-config'

export const SIDEBAR_PAGE_METADATA_USER_EDIT_WRITE: {
  status: DocumentPageMetadataUserEditStatus
  source: DocumentPageMetadataUserEditSource
  surface: DocumentPageMetadataUserEditSurface
} = {
  status: 'confirmed',
  source: 'editor',
  surface: 'sidebar',
}

export const EVAL_PAGE_METADATA_USER_EDIT_WRITE: {
  source: DocumentPageMetadataUserEditSource
  surface: DocumentPageMetadataUserEditSurface
} = {
  source: 'editor',
  surface: 'eval',
}

export type DocumentPageMetadataUserEditPutData = {
  document_id: string
  page_id: string
  page_num: number
  system_document_page_entry_id: string | null
  values: DocumentPageMetadataUserEditValues
  generated_values: DocumentPageMetadataUserEditValues
  status: DocumentPageMetadataUserEditStatus
  source: DocumentPageMetadataUserEditSource
  surface: DocumentPageMetadataUserEditSurface
  pipeline_config_id: string | null
  defer_reason: string | null
  should_have_metadata: boolean | null
  comments?: DocumentPageMetadataUserEditComment[]
  blind_qa?: boolean
}

export type BuildDocumentPageMetadataUserEditPutArgs = {
  siteId: string
  organizationId: string
  bqDocumentId: string
  pageNum: number
  systemPage: SystemDocumentPageEntry | undefined
  priorEdit: DocumentPageMetadataUserEditEntry | undefined
  userValues: DocumentPageMetadataUserEditValues
  status: DocumentPageMetadataUserEditStatus
  source: DocumentPageMetadataUserEditSource
  surface: DocumentPageMetadataUserEditSurface
  pipelineConfigId: string | null
  deferReason?: string | null
  shouldHaveMetadata?: boolean | null
  comments?: readonly DocumentPageMetadataUserEditComment[]
  blindQa?: boolean
}

export function resolvePageMetadataPipelineConfigId(
  systemPage: SystemDocumentPageEntry | undefined,
  selectedPipelineConfigId: string | undefined
): string | null {
  const tagged = systemPage?.entry.tags?.[PIPELINE_CONFIG_TAG]
  if (typeof tagged === 'string' && tagged.length > 0) {
    return tagged
  }
  if (
    typeof selectedPipelineConfigId === 'string' &&
    selectedPipelineConfigId.length > 0
  ) {
    return selectedPipelineConfigId
  }
  return null
}

export function buildDocumentPageMetadataUserEditPut(
  args: BuildDocumentPageMetadataUserEditPutArgs
): RolodexEntryPutJson<DocumentPageMetadataUserEditPutData> {
  const {
    siteId,
    organizationId,
    bqDocumentId,
    pageNum,
    systemPage,
    priorEdit,
    userValues,
    status,
    source,
    surface,
    pipelineConfigId,
    deferReason,
    shouldHaveMetadata,
    comments,
    blindQa,
  } = args
  const writeComments =
    comments === undefined || comments.length === 0 ? undefined : [...comments]

  const pageId =
    systemPage?.entry.data.page_id ?? bareDocumentPageId(bqDocumentId, pageNum)
  // Seed from generated + overlay so a sparse cascade patch cannot persist
  // omitted keys as user-clears (null). Confirmed events still snapshot every
  // allowlisted key, including explicit nulls for fields that were never set.
  const values = buildFullDocumentPageMetadataUserEditValues({
    ...mergePageMetadataValues(
      systemPage?.entry.data,
      priorEdit?.entry.data.values
    ),
    ...buildDocumentPageMetadataUserEditValues(userValues),
  })

  return {
    id: crypto.randomUUID(),
    type: RolodexEntryType.DOCUMENT_PAGE_METADATA_USER_EDIT,
    data: {
      document_id: bqDocumentId,
      page_id: pageId,
      page_num: pageNum,
      system_document_page_entry_id: systemPage?.entry.id ?? null,
      values,
      generated_values: buildGeneratedPageMetadataValues(
        systemPage?.entry.data
      ),
      status,
      source,
      surface,
      pipeline_config_id: pipelineConfigId,
      defer_reason: status === 'deferred' ? deferReason?.trim() || null : null,
      should_have_metadata: shouldHaveMetadata ?? null,
      ...(writeComments === undefined ? {} : { comments: writeComments }),
      ...(blindQa === true ? { blind_qa: true } : {}),
    },
    tags: {
      organization: organizationId,
      site: siteId,
      'cantilever-document': cantileverDocumentEntryId(siteId, bqDocumentId),
      'cantilever-document-page': cantileverDocumentPageEntryId(siteId, pageId),
    },
  }
}
